/** Subset of the API response surface the embed actually uses. */

// The tree is two levels deep. `level` says what a region is, not how deep it
// sits: "district" is the top level (the 12 Bezirke and the "Umland"
// grouping), "subdistrict" and "municipality" are both children of one.
export type RegionLevel = "district" | "subdistrict" | "municipality";

/** The synthetic top-level row grouping the Brandenburg municipalities that
 *  border Berlin. Mirrors Region.UMLAND_SLUG in the backend. */
export const UMLAND_SLUG = "umland";

/** True for the levels that sit below a top-level entry. */
export function isSubRegion(level: RegionLevel): boolean {
  return level === "subdistrict" || level === "municipality";
}

export interface PublicRegion {
  slug: string;
  name: string;
  level: RegionLevel;
  parent_slug: string | null;
}

/** Inline org fields present in Berlin-wide list responses. */
export interface InlineOrg {
  slug: string;
  name: string;
  color_slug: string;
  logo_url: string;
}

/** Accepted participating initiative on a co-organized action (#99). */
export interface CoOrganizer {
  slug: string;
  name: string;
  color_slug: string;
  logo_url: string;
}

export interface PublicAction {
  id: string;
  start_at: string;
  duration_minutes: number | null;
  title: string;
  meeting_point: string;
  status: "published" | "cancelled" | "completed" | "draft";
  intro_text: string;
  cover_photo_url: string;
  participation_enabled: boolean;
  /** Present on the org-scoped list since M6.8 (map markers). Null = ungeocoded. */
  lat?: string | null;
  lng?: string | null;
  /** Present in Berlin-wide list responses (PublicActionListWithOrgSerializer). */
  org?: InlineOrg;
  /** Accepted co-organizers (#99). Empty/absent for solo actions. */
  co_organizers?: CoOrganizer[];
}

export interface PaginatedActions {
  next: string | null;
  previous: string | null;
  results: PublicAction[];
}

/** One upcoming-action line on a directory tile (#158). Opens the Lightbox by id. */
export interface DirectoryNextAction {
  id: string;
  start_at: string;
  title: string;
  meeting_point: string;
}

/** An initiative row in the directory embed (GET /api/orgs/?include=next_actions). */
export interface DirectoryOrg {
  id: string;
  slug: string;
  name: string;
  description: string;
  active_area: string;
  meeting_schedule: string;
  color_slug: string;
  logo_url: string;
  scope_label: string;
  home_region: {
    slug: string;
    name: string;
    level: RegionLevel;
    parent_name: string | null;
    parent_slug: string | null;
  } | null;
  website_url: string;
  whatsapp_url: string;
  signal_url: string;
  telegram_url: string;
  instagram_url: string;
  nebenan_url: string;
  facebook_url: string;
  linktree_url: string;
  next_actions: DirectoryNextAction[];
}

export interface PublicOrg {
  slug: string;
  name: string;
  color_slug: string;
  logo_url: string;
  website_url: string;
  whatsapp_url: string;
  signal_url: string;
  telegram_url: string;
  instagram_url: string;
  nebenan_url: string;
  facebook_url: string;
  linktree_url: string;
}

export interface PublicReportPhoto {
  image_url: string;
  is_exotic: boolean;
  order: number;
}

/** Post-event report (M8.1) — shown in the Lightbox for completed actions. */
export interface PublicActionReport {
  participants: number | null;
  children: number | null;
  bags_120l: number | null;
  kilograms: string | null;
  cigarette_butts: number | null;
  bottle_caps: number | null;
  single_use_cups: number | null;
  bottles: number | null;
  tree_pits_cleaned: number | null;
  bulky_waste_cases: number | null;
  exotic_find_text: string;
  narrative_text: string;
  photos: PublicReportPhoto[];
}

export interface PublicActionDetail {
  id: string;
  start_at: string;
  duration_minutes: number | null;
  title: string;
  meeting_point: string;
  address: string;
  lat: string | null;
  lng: string | null;
  status: "published" | "cancelled" | "completed" | "draft";
  provided: string[];
  bring_along: string[];
  cover_photo_url: string;
  cancelled_reason: string;
  report: PublicActionReport | null;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  translation: {
    intro_text?: string;
    what_to_expect?: string;
    accessibility_notes?: string;
    weather_policy?: string;
    waste_disposal_note?: string;
    contact_questions?: string;
  };
  org: PublicOrg & { participation_enabled: boolean };
  participation: ParticipationCounts;
  co_organizers?: CoOrganizer[];
}

export interface ParticipationCounts {
  yes: number;
  maybe: number;
}

export type ParticipationStatus = "yes" | "maybe" | "none";

/** How many events to show: a count, or a time window in weeks/months. */
export type EventFilter =
  | { mode: "count"; value: number }   // value=0 means all
  | { mode: "weeks"; value: number }
  | { mode: "months"; value: number };

export interface EmbedConfig {
  /** UI language. From data-cleanups-locale; default "de", no auto-detect. */
  locale: "de" | "en";
  /** Org slug for org-scoped mode. Absent in Berlin-wide mode. */
  org?: string;
  /**
   * Region slug for district-scoped mode (data-cleanups-region). Pins the
   * city-wide embed to one district/subdistrict and hides the region filter.
   * Absent in org-scoped and unfiltered Berlin-wide mode.
   */
  region?: string;
  apiBase: string;
  /** How many / which upcoming events to show. */
  upcoming: EventFilter;
  /** If null, past events are hidden. */
  past: EventFilter | null;
  shareBase?: string;
  /**
   * Override for the footer "see all" link (data-cleanups-more-url). When set,
   * the footer links here instead of cleanups.berlin and keeps a small
   * "provided by cleanups.berlin" attribution below it.
   */
  moreUrl?: string;
  /** Default display mode for the action list. */
  view?: "list" | "tile";
  /** Show a map above the list/tile (optional layer, not a separate view). */
  showMap?: boolean;
  /** Directory mode (data-cleanups-initiatives): initiative tiles, not an event list. */
  initiatives?: boolean;
  /**
   * Where the district/subdistrict filter sits relative to the map in
   * Berlin-wide mode. "above" (default) renders it before the map, "below"
   * between the map and the list. Only has a visible effect when the map is
   * shown; without a map the filter always precedes the list.
   */
  filterPosition?: "above" | "below";
  /** Fixed column count in tile/directory view (1–6). Absent = auto-fill. */
  columns?: 1 | 2 | 3 | 4 | 5 | 6;
  /** Absolute base URL for self-hosted Leaflet assets (ends in `/`). Map only. */
  vendorBase?: string;
}
